import { useState, useEffect, useCallback } from 'react';
import { formatDay } from '@/lib/restaurantTime';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  DollarSign, ShoppingBag, TrendingUp, TrendingDown, Users,
  ArrowUpRight, ArrowDownRight, CreditCard, Banknote, Clock,
  BarChart3, Store, Calendar, RefreshCw, ChevronRight, Star,
  MapPin, Building2, Activity, Filter, ChevronDown, Wallet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { IncidentAlertWidget } from '@/components/incident-audit';
import { ReliabilityQuickWidget } from '@/components/reliability';
import { AlertQuickWidget } from '@/components/reliability-alerts';
import KioskFleetTile from '@/components/backoffice/KioskFleetTile';
import KioskReliabilityTile from '@/components/backoffice/KioskReliabilityTile';

import { API } from '@/config/api';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// Simple Bar Chart Component for Payment Methods
function PaymentChart({ data }) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
  
  return (
    <div className="flex items-end gap-3 h-32">
      {data.map((item, index) => {
        const height = (item.value / maxValue) * 100;
        return (
          <div key={item.label} className="flex flex-col items-center flex-1">
            <div className="w-full flex flex-col items-center justify-end h-24">
              <div 
                className="w-12 rounded-t transition-all duration-500"
                style={{ 
                  height: `${Math.max(height, 5)}%`,
                  backgroundColor: colors[index % colors.length]
                }}
              />
            </div>
            <span className="text-xs text-slate-500 mt-2">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// Location Sales Item
function LocationSalesItem({ location, rank, isHighest }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
          rank <= 3 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
        }`}>
          {rank}
        </div>
        <div>
          <p className="font-medium text-slate-800">{location.name}</p>
          <p className="text-sm text-slate-500">{location.address || 'No address'}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold text-slate-900">{formatCurrency(location.net_sales)}</p>
        {location.order_count && (
          <p className="text-xs text-slate-500">{location.order_count} orders</p>
        )}
      </div>
    </div>
  );
}

export default function DashboardHome() {
  // Safe destructuring with defaults
  const context = useOutletContext() || {};
  const { 
    token = localStorage.getItem('backoffice_token'), 
    manager = null, 
    restaurant = null, 
    activeRestaurantId = null, 
    restaurants = [] 
  } = context;
  const navigate = useNavigate();
  
  // State
  const [dateRange, setDateRange] = useState('today');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    netSales: 0,
    avgOrderValue: 0,
    totalOrders: 0,
    cardPayments: 0,
    cashPayments: 0,
    otherPayments: 0
  });
  const [locationSales, setLocationSales] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [period, setPeriod] = useState(null); // the restaurant's own days, from the server
  const [activityLog, setActivityLog] = useState([]);
  const [salesSortTab, setSalesSortTab] = useState('highest');
  const [loadError, setLoadError] = useState(false);

  // Determine if multi-location view
  const isMultiLocation = restaurants.length > 1;
  const activeRestaurantName = restaurants.find(r => r.id === activeRestaurantId)?.name || restaurant?.name || 'All Locations';

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // Fetch multi-location stats
      const response = await axios.get(`${API}/merchant/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          date_range: dateRange,
          restaurant_id: activeRestaurantId
        }
      });
      
      setStats(response.data.stats || {});
      setLocationSales(response.data.location_sales || []);
      setRecentOrders(response.data.recent_orders || []);
      setPeriod(response.data.period || null);
      setActivityLog(response.data.activity_log || []);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Could not load dashboard data');
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, activeRestaurantId]);

  useEffect(() => {
    if (token) {
      fetchDashboardData();
    }
  }, [token, fetchDashboardData]);

  const getDateRangeLabel = () => {
    if (period?.first_day) {
      // the restaurant's calendar days (its time zone), not the browser's
      return period.first_day === period.last_day ? formatDay(period.first_day)
        : `${formatDay(period.first_day, { weekday: undefined, year: undefined })} - ${formatDay(period.last_day, { weekday: undefined })}`;
    }
    const today = new Date();
    if (dateRange === 'today') {
      return today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } else if (dateRange === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } else {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return `${weekAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  };

  const sortedLocationSales = [...locationSales].sort((a, b) => 
    salesSortTab === 'highest' ? b.net_sales - a.net_sales : a.net_sales - b.net_sales
  );

  const paymentChartData = [
    { label: 'Card', value: stats.cardPayments || 0 },
    { label: 'Cash', value: stats.cashPayments || 0 },
    { label: 'Other', value: stats.otherPayments || 0 }
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-home">
      {/* Header with Date Range */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 md:text-2xl">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5 md:text-base">{getDateRangeLabel()}</p>
        </div>
        
        {/* Date Range Selector */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[120px] sm:w-[140px] bg-white h-9 sm:h-10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchDashboardData}
            className="bg-white h-9 w-9 sm:h-10 sm:w-10 min-w-[36px]"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="dashboard-load-error">
          <span>Could not load dashboard data. The figures below are not current.</span>
          <Button variant="outline" size="sm" onClick={fetchDashboardData}>Try again</Button>
        </div>
      )}

      {/* Stats Grid - Responsive: 2 cols on mobile, 3 on tablet */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500 mb-1">Net Sales</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{formatCurrency(stats.netSales)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500 mb-1">Total Orders</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{stats.totalOrders || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm col-span-2 sm:col-span-1">
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500 mb-1">Avg Order</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">{formatCurrency(stats.avgOrderValue)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Incident Alert Widget */}
      <IncidentAlertWidget />

      {/* Reliability Quick Widget */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        <ReliabilityQuickWidget />
        
        {/* Alert Quick Widget */}
        <AlertQuickWidget showEvaluate={true} />
        
        {/* Kiosk Fleet Tile */}
        <KioskFleetTile />
        
        {/* Kiosk Reliability Tile */}
        <KioskReliabilityTile />
        
        {/* Quick Links for Support Tools */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="text-sm sm:text-base font-semibold text-gray-800">Support Tools</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <div className="grid grid-cols-2 gap-3">
              <a
                href={bo('reliability-alerts')}
                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center"
                data-testid="dashboard-link-alerts"
              >
                <Activity className="h-6 w-6 mx-auto mb-2 text-red-500" />
                <p className="text-sm font-medium">Alerts</p>
              </a>
              <a
                href={bo('support-diagnostics')}
                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center"
                data-testid="dashboard-link-diagnostics"
              >
                <BarChart3 className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                <p className="text-sm font-medium">Diagnostics</p>
              </a>
              <a
                href={bo('incident-audit')}
                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center"
                data-testid="dashboard-link-incidents"
              >
                <Activity className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
                <p className="text-sm font-medium">Incidents</p>
              </a>
              <a
                href={bo('pos-reconciliation')}
                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center"
                data-testid="dashboard-link-pos"
              >
                <Store className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                <p className="text-sm font-medium">POS Recovery</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Grid - Stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Payment Methods */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="text-sm sm:text-base font-semibold text-gray-800">Payment Methods</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <PaymentChart data={paymentChartData} />
            <div className="mt-3 sm:mt-4 space-y-2">
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-blue-500"></div>
                  <span className="text-gray-600">Card</span>
                </div>
                <span className="font-medium">{formatCurrency(stats.cardPayments)}</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-green-500"></div>
                  <span className="text-gray-600">Cash</span>
                </div>
                <span className="font-medium">{formatCurrency(stats.cashPayments)}</span>
              </div>
              <div className="flex items-center justify-between text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-yellow-500"></div>
                  <span className="text-gray-600">Other</span>
                </div>
                <span className="font-medium">{formatCurrency(stats.otherPayments)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Locations OR Recent Activity */}
        {isMultiLocation && locationSales.length > 0 ? (
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
              <CardTitle className="text-sm sm:text-base font-semibold text-gray-800">Top Locations</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="space-y-3">
                {sortedLocationSales.slice(0, 5).map((loc) => {
                  const maxSales = sortedLocationSales[0]?.net_sales || 1;
                  const width = (loc.net_sales / maxSales) * 100;
                  return (
                    <div key={loc.id} className="space-y-1">
                      <div className="flex justify-between text-xs sm:text-sm gap-2">
                        <span className="text-gray-700 truncate min-w-0">{loc.name}</span>
                        <span className="font-medium text-gray-900 flex-shrink-0">{formatCurrency(loc.net_sales)}</span>
                      </div>
                      <div className="h-1.5 sm:h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
              <CardTitle className="text-sm sm:text-base font-semibold text-gray-800">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="space-y-3">
                {activityLog.length === 0 && (
                  <p className="text-xs sm:text-sm text-gray-400">No recent activity</p>
                )}
                {activityLog.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-blue-500 mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <div className="min-w-0">
                      <p className="text-gray-700 truncate">{activity.message}</p>
                      <p className="text-gray-400 text-xs">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Actions - Mobile only, hidden on xl+ where sidebar shows this */}
      <Card className="bg-white border-0 shadow-sm xl:hidden">
        <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
          <CardTitle className="text-sm sm:text-base font-semibold text-gray-800">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              className="flex-col h-auto py-3 sm:py-4 text-gray-600 hover:text-blue-600 hover:border-blue-600 text-xs sm:text-sm"
              onClick={() => navigate(bo('orders'))}
            >
              <ShoppingBag className="h-4 w-4 sm:h-5 sm:w-5 mb-1" />
              Orders
            </Button>
            <Button 
              variant="outline" 
              className="flex-col h-auto py-3 sm:py-4 text-gray-600 hover:text-blue-600 hover:border-blue-600 text-xs sm:text-sm"
              onClick={() => navigate(bo('sales'))}
            >
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 mb-1" />
              Sales
            </Button>
            <Button 
              variant="outline" 
              className="flex-col h-auto py-3 sm:py-4 text-gray-600 hover:text-blue-600 hover:border-blue-600 text-xs sm:text-sm"
              onClick={() => navigate(bo('settings'))}
            >
              <Wallet className="h-4 w-4 sm:h-5 sm:w-5 mb-1" />
              Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Panel - Only visible on xl+ screens (same as before but in a row) */}
      <div className="hidden xl:grid xl:grid-cols-2 gap-6">
        {/* User Greeting Card */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                {manager?.full_name?.charAt(0) || 'M'}
              </div>
              <div className="min-w-0">
                <p className="text-gray-500 text-sm">Welcome back,</p>
                <p className="font-semibold text-gray-900 truncate">{manager?.full_name?.split(' ')[0] || 'Manager'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Store Stats */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Store className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-600">Stores</span>
              </div>
              <span className="font-bold text-blue-600 text-xl">{restaurants.length || (restaurant ? 1 : 0)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
